import type { NoticeIcon } from "@/lib/brokerNotifications";
import type { DocumentIcon } from "@/lib/brokerDocumentTypes";

// BRK-S188 — the small pictures beside an inbox row's kind and a document's
// type. Decorative: the label beside each icon is the accessible text, so the
// SVG is `aria-hidden`. `data-icon` names the picture for tests.
//
// Kept tiny and local, as the SideNav icons are: stroke paths on a 24px grid in
// `currentColor`, so each inherits the text colour beside it.

export type KindIconName = NoticeIcon | DocumentIcon;

const PATHS: Record<KindIconName, React.ReactNode> = {
  // A bell: the neutral "Update" for a notice type this build cannot name.
  update: (
    <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15L6 16zM10 20.5a2 2 0 0 0 4 0" />
  ),
  // A flag on a pole: a bid.
  bid: <path d="M5 21V4M5 4h11l-2 4 2 4H5" />,
  // A pen stroke over a line: a signature.
  signature: <path d="M4 19h16M6 15l8.5-8.5a2.1 2.1 0 0 1 3 3L9 18H6v-3z" />,
  // A box: a packet of documents.
  packet: <path d="M4 8l8-4 8 4v8l-8 4-8-4V8zM4 8l8 4 8-4M12 12v8" />,
  // A receipt with a zig-zag foot.
  invoice: (
    <path d="M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21V3zM9 8h6M9 12h6" />
  ),
  // A triangle with a bang.
  warning: <path d="M12 4l9 16H3l9-16zM12 10v4M12 17v.5" />,
  // A circled tick.
  paid: (
    <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12.5l2.5 2.5L16 9.5" />
  ),
  // A price tag: a quote (BRK-S466).
  quote: (
    <path d="M3 12V4h8l10 10-8 8L3 12zM7.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" />
  ),
  // A shield with a tick: bound coverage, a binder or a policy.
  bound: (
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3zM9 12l2 2 4-4" />
  ),
  // A magnifier: the underwriter's review.
  review: (
    <path d="M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13zM15 15l5 5" />
  ),
  // A speech bubble: Signal's request, and its closing.
  reply: <path d="M4 5h16v11H9l-5 4V5z" />,
  // A list with ticks: a condition on the coverage.
  condition: (
    <path d="M4 6l1.5 1.5L8 5M4 12l1.5 1.5L8 11M4 18l1.5 1.5L8 17M11 6h9M11 12h9M11 18h9" />
  ),
  // A page with a folded corner: the neutral "Document".
  document: <path d="M7 3h7l4 4v14H7V3zM14 3v4h4" />,
  // An envelope: a letter.
  letter: <path d="M3 6h18v12H3V6zM3 6l9 7 9-7" />,
  // A bar chart: results.
  results: <path d="M4 20h16M7 20v-6M12 20V8M17 20v-9" />,
};

export function KindIcon({ name }: { name: KindIconName }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-icon={name}
      className="shrink-0"
    >
      {PATHS[name]}
    </svg>
  );
}
